import { Component, OnInit, EventEmitter, Output, OnDestroy, HostBinding } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms'
import states from '@app/constants/state-list.json'
import { WorkflowStep } from '@app/models/workflow-step'
import { StoreService } from '@app/services/store.service'
import { Observable, Subject } from 'rxjs'
import { map, filter, startWith, takeUntil } from 'rxjs/operators'

// prettier-ignore
interface IState {
	abbreviation: string;
	name: string;
}

@Component({
	selector: 'mv-new-vet',
	templateUrl: './new-vet.component.html',
	styleUrls: ['./new-vet.component.scss'],
})
export class NewVetComponent implements OnInit, OnDestroy {
	@HostBinding('[data-emb-element]') embAttribute = true
	@Output() _emb_close: EventEmitter<boolean> = new EventEmitter<boolean>()
	public newClinicForm: FormGroup
	public states: IState[] = states
	public petName$: Observable<string>
	public filteredStates$?: Observable<IState[]>
	private readonly zipLength: number = 5
	private readonly phoneLength: number = 10
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject()

	constructor(private readonly fb: FormBuilder, private readonly storeService: StoreService) {
		this.petName$ = this.storeService.appState$.pipe(
			filter(s => s.petId !== undefined),
			map(s => s.petName ?? 'Your Pet')
		)

		this.newClinicForm = this.fb.group({
			name: [null, [Validators.required, Validators.pattern(/[\w\s+]/g)]],
			streetAddress: [null, [Validators.required, Validators.pattern(/[\w\s+]/g)]],
			zipCode: [
				null,
				[
					Validators.required,
					Validators.minLength(this.zipLength),
					Validators.maxLength(this.zipLength),
					Validators.pattern(/\d/g),
				],
			],
			// NOTE: Possibly make a validator to tell the user to select a value rather than type one
			state: [null, Validators.required],
			phone: [null, [Validators.minLength(this.phoneLength), Validators.maxLength(this.phoneLength), Validators.pattern(/\d/g)]],
		})
	}

	private get stateControl(): AbstractControl | null {
		return this.newClinicForm.get('state')
	}

	ngOnInit(): void {
		this.filteredStates$ = this.stateControl?.valueChanges.pipe(
			startWith(''),
			map(v => this.stateFilter(<string>v)),
			takeUntil(this.ngUnsubscribe$)
		)
	}

	public checkForSelection(): void {
		console.warn('checkForSelection not yet set up')
	}

	public stepBack(): void {
		this.storeService.updateWorkflowStep(WorkflowStep.VetClinic)
	}

	public nextStep(): void {
		this.storeService.updateFormState({
			cannotFindVet: true,
			newVetClinic: {
				name: this.newClinicForm.get('name')?.value,
				address: this.newClinicForm.get('streetAddress')?.value,
				zipCode: this.newClinicForm.get('zipCode')?.value,
				state: this.newClinicForm.get('state')?.value,
				phoneNumber: this.newClinicForm.get('phone')?.value,
			},
		})
		this.storeService.updateWorkflowStep(WorkflowStep.OtherOwners)
	}

	public cancel(): void {
		this._emb_close.emit(false)
	}

	private stateFilter(value: string): IState[] {
		const filterValue = this.normalizeValue(value)

		return this.states.filter(s => this.normalizeValue(s.name).includes(filterValue))
	}

	private normalizeValue(value: string): string {
		return value.toLowerCase().replace(/\s/g, '')
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true)
		this.ngUnsubscribe$.unsubscribe()
	}
}
